import { addToWindowRef, ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { HpAnswerMap, HpSubmitFormActionProps, setCustomerAnswer } from '@avantia/questionset-model';
import { addRumAction, setRumGlobalContext } from '@avantia/third-party-monitoring/configureDataDogRum';
import { logError } from '../clientLogging';
import { HpClaimsQuestionSetPageStateProps } from '../components/questionSetForm/questionSetPageTypes';
import { getConfig } from '../config/configuration';
import { fetchResolver } from '../engineClient/actions/fetchResolver';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { getValidationForEachSection } from '../engineClient/reducers/getValidationForEachSection';
import { HpFieldsFailedValidationPayload } from '../engineClient/reducers/questionSetReducer';
import { evaluateSectionPrerequisites } from '../engineClient/reducers/questionSetTools';
import { applyChanges } from '../engineClient/reducers/reducerLibrary';
import {
  getAnswersFromAnswerMap,
  getMetaDataOrUndefinedFromAnswers
} from '../engineClient/tools/clientAndServerUtilities';
import { getNextOrPreviousSectionId } from '../engineClient/tools/questionDisplayStateTools';
import { isAgent } from '../engineClient/tools/utilities';
import { HpAction, HpCreateActionPromisesProps, HpDependentActionFunction } from '../engineClient/types';
import { Policy } from '../types/policyTypes';
import { questionIds } from '../types/questionConstants';
import {
  ClaimEnquiryResultCopyText,
  fnolPhase,
  HpClaimEnquiryResultState,
  HpClaimsMetaData
} from '../types/stateTypes';

let environment: string;
let dependentActionTimeoutMs: number;
registerConfigurationAction((config) => {
  ({ environment, dependentActionTimeoutMs } = config);
});
const { isClaimsRoutingEnabled, siteViewRedirectUrl } = getConfig();

type SubmitClaimDependentActionProps = ClaimEnquiryResultCopyText & HpSubmitFormActionProps;
interface HpClaimSubmittedResponse {
  claimsReferenceNumber: string;
  siteViewMeetingId?: string;
  answers: HpAnswerMap;
}

export interface HpClaimUpdatedResponse {
  success: boolean;
  patchedDataCount: number;
  failedDataCount: number;
  submittedStepCount: number;
}

export interface HpSubmitFormResult {
  success: boolean;
}

/**
 * A failed submit is logged as an object, so its fields stay searchable rather than being flattened into a
 * message, and is shown on screen outside live, where a dependent action that fails quietly looks to whoever is
 * testing like nothing happened at all.
 */
function reportSubmitFailure(message: string, reason: unknown, answersId: string): void {
  logError({ message, reason, answersId });

  if (['developer', 'dev', 'tst'].indexOf(environment) >= 0) {
    const detail = typeof reason === 'string' ? reason : JSON.stringify(reason, null, 2);
    alert(`${message}\n\n${detail}`);
  }
}

function hasNextVisibleSection(baseState: HpClaimsQuestionSetPageStateProps, answerUpdates: HpAnswerMap): boolean {
  const updatedAnswers = { ...baseState.answers, ...answerUpdates };
  const mergedState = applyChanges(baseState, { answers: updatedAnswers }) as any;
  mergedState.displayState.sectionsUI = evaluateSectionPrerequisites(mergedState.displayState, mergedState, 'none');
  const { activeSectionId } = mergedState.displayState;
  const nextSectionId = getNextOrPreviousSectionId(mergedState.displayState, 'next', activeSectionId);
  return nextSectionId !== activeSectionId;
}

interface HpSubmitClaimActionPayload {
  detail: string;
  addressValidation: string;
  questionId: string;
}

export function submitClaimAction(
  properties: HpCreateActionPromisesProps
): HpDependentActionFunction<unknown> | undefined {
  const { questionSet } = properties;
  const payload = properties.payload as unknown as HpSubmitClaimActionPayload;
  ensureDefined(payload, 'payload');
  const { detail, addressValidation, questionId } = payload;
  // TODO FIX - This needs to be done as dynamic button fields trigger fieldChanged which also calls dependent actions.
  if (detail || addressValidation) {
    return;
  }

  const { answers, answersId, questionSetId, productId } = questionSet;
  const newAnswers = { ...answers };
  // We need to set this here as the form is submitted before the reducer updates the answers.
  setCustomerAnswer(newAnswers, questionId, true);
  const newState = applyChanges(questionSet, { answers: newAnswers }) as unknown as HpClaimsQuestionSetPageStateProps;
  let policy: Policy | undefined;
  const policies = newState.policies;
  const { policyInstance } = getAnswersFromAnswerMap({
    answers: newAnswers,
    keys: ['policyInstance'],
    productId
  });
  if (policies && policyInstance) {
    policy = policies[policyInstance];
  }

  const data: SMap<any> = {
    questionSetId,
    answers: newAnswers,
    answersId,
    productId,
    policy,
    scoringMap: newState.scoringMap
  };

  const validateFieldsFunc = (): HpFieldsFailedValidationPayload | undefined => {
    let result: HpFieldsFailedValidationPayload | undefined;
    // Cast to the correct type for the evaluation
    const clientState = newState as any;
    clientState.displayState.sectionsUI = evaluateSectionPrerequisites(clientState.displayState, clientState, 'none');
    const { sectionValidationMap, firstSectionIdWithErrors, validSections } = getValidationForEachSection({
      state: clientState,
      raiseNavigationChangedEvent: false
    });

    if (firstSectionIdWithErrors) {
      result = {
        sectionIdWithErrors: firstSectionIdWithErrors,
        validation: {
          ...questionSet.validationResult,
          ...sectionValidationMap[firstSectionIdWithErrors],
          validSections
        },
        scrollTo: 'top' as any
      };
    }

    return result;
  };

  const submitFormAction: HpDependentActionFunction<unknown> = (dispatch) => {
    const { dependentAction } = properties;
    const { props } = dependentAction;
    const claimEnquiryResult = props as unknown as SubmitClaimDependentActionProps;
    const { submissionType, continueOnSuccess = true, validateFields = true } = claimEnquiryResult;
    const isClaim = submissionType === 'claim';
    addToWindowRef({ submitFormResult: null });

    const {
      claimReference: existingClaimReference,
      journeyPhase: currentJourneyPhase,
      claimEnquiryReference
    } = getAnswersFromAnswerMap({
      answers: newAnswers,
      keys: ['claimReference', 'journeyPhase', 'claimEnquiryReference'],
      productId
    });
    // An agent returning from the enquiry review; the claim already exists so we update it in SiteView
    // and move back into the FNOL phase without re-submitting.
    const isReturningToFnol = isClaimsRoutingEnabled && !!existingClaimReference && currentJourneyPhase !== fnolPhase;

    const advanceAnswers: HpAnswerMap = {};
    setCustomerAnswer(advanceAnswers, questionIds.journeyPhase, fnolPhase);
    const hasFnolPhase = hasNextVisibleSection(newState, advanceAnswers);
    data.hasFnolPhase = hasFnolPhase;

    return new Promise<boolean>((resolve, reject) => {
      // Both submitting and updating validate first, so an agent sees which answer is wrong rather than a
      // request that quietly achieves nothing.
      if (validateFields) {
        const validation = validateFieldsFunc();
        if (validation) {
          dispatch({ type: 'FIELDS_FAILED_VALIDATION', payload: validation });
          return resolve(false);
        }
      }

      if (isReturningToFnol) {
        return fetchResolver<unknown, HpClaimUpdatedResponse>({
          options: {
            url: '/api/claim/enquiry-details',
            data,
            method: 'POST',
            description: 'Dependent action update-claim',
            timeout: dependentActionTimeoutMs
          },
          loadingPageType: 'page',
          dispatch,
          successAction: (resp) => {
            if (resp.success) {
              if (hasFnolPhase) {
                dispatch({ type: 'CLAIM_ADVANCE_TO_NEXT_SECTION' as any, payload: { answers: advanceAnswers } });
              } else {
                const claimSubmittedPayload: HpClaimSubmittedActionPayload = {
                  ...claimEnquiryResult,
                  claimReferenceNumber: existingClaimReference || claimEnquiryReference,
                  claimJourneyComplete: true
                };
                dispatch({
                  type: 'CLAIM_SUBMITTED' as any,
                  payload: claimSubmittedPayload
                } as HpClaimSubmittedAction);
              }
            }

            // Resolved from the response itself: whether the journey advanced is only known here, and
            // fetchResolver's promise carries no value.
            resolve(resp.success);
          },
          failedAction: (reason) => {
            reportSubmitFailure('Failed to update claim', reason, answersId);
            reject(reason);
          }
        });
      }

      fetchResolver<unknown, HpClaimSubmittedResponse>({
        options: {
          url: `/api/claim?isClaim=${isClaim}&validateAnswers=${validateFields}`,
          data,
          method: 'POST',
          description: `Dependent action submit-claim`,
          timeout: dependentActionTimeoutMs
        },
        loadingPageType: 'page',
        dispatch,
        successAction: (resp) => {
          // TODO Something dynamic TBD.
          addToWindowRef({ submitFormResult: resp });
          // const { timeTaken, answers } = resp;
          // TODO AL - Use HpClaimsActionTypes instead and use answersId as reference until real claim reference when API is ready.
          const isComplete = !(isClaimsRoutingEnabled && hasFnolPhase);
          const claimSubmittedPayload: HpClaimSubmittedActionPayload = {
            ...claimEnquiryResult,
            claimReferenceNumber: resp.claimsReferenceNumber,
            claimJourneyComplete: isComplete
          };

          const action = {
            type: 'CLAIM_SUBMITTED' as any,
            payload: claimSubmittedPayload
          } as HpClaimSubmittedAction;
          dispatch(action);
          setRumGlobalContext('claimEnquiryReference', resp.claimsReferenceNumber);
          addRumAction('claim-submitted', { answersId, claimEnquiryReference: resp.claimsReferenceNumber });

          if (isClaimsRoutingEnabled) {
            setCustomerAnswer(resp.answers, questionIds.journeyPhase, fnolPhase);
          }

          if (!isComplete) {
            dispatch({ type: 'CLAIM_ADVANCE_TO_NEXT_SECTION' as any, payload: { answers: resp.answers } });
          } else {
            // Only redirect an agent to SiteView once the journey is complete (no next section). The
            // first EoW submit reveals the peril sections, so redirecting there would skip them; the
            // redirect now fires only on the final submission.
            const metadata: HpClaimsMetaData | undefined = getMetaDataOrUndefinedFromAnswers(productId, resp.answers);
            if (metadata) {
              const { siteViewMeetingId } = metadata;
              if (isAgent(metadata.agentId) && siteViewMeetingId) {
                window.location.href = siteViewRedirectUrl + siteViewMeetingId;
              }
            }
          }
        },
        failedAction: (reason) => reportSubmitFailure('Failed to save claim', reason, answersId)
      })
        .then(() => resolve(continueOnSuccess ? true : false))
        .catch(reject);
    });
  };

  return submitFormAction;
}

export type HpClaimSubmittedActionPayload = HpClaimEnquiryResultState;

export type HpClaimSubmittedAction = HpAction<HpClaimSubmittedActionPayload>;
